'use client';
import React, { FC, useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Head from 'next/head';

interface Props {
  isSideBar?: boolean;
}

const NewsLetter: FC<Props> = ({ isSideBar }) => {
  const [mounted, setMounted] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setHasError(false);
    setMessage('');

    try {
      const response = await fetch('/api/newsletters/updatesubscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          ListIds: ['529'], // Default newsletter ID
          params: {
            utm_source: 'newsletter_sidebar',
            utm_medium: 'web',
          },
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to subscribe');
      }

      setMessage('Successfully subscribed to newsletter');
      setEmail('');
    } catch (error) {
      setHasError(true);
      setMessage('Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    mounted && (
      <>
        <Head>
          <link href="/css/components/newsLetter.css" rel="stylesheet" />
        </Head>
        <div
          className={`newsletter ${isSideBar ? 'newsletter-sidebar' : ''}`}
          data-testid={isSideBar && 'newsletter-sidebar'}
        >
          {isSideBar && (
            <div className="newsletter-img-container">
              <Image
                src="/img/news-letter.png"
                alt="Woman using a tablet"
                height={359}
                width={525}
              />
              <div className="newsletter-icon">
                <img
                  src="/img/icon-send.svg"
                  alt=""
                  /* decorative */
                  data-testid="icon-newsletter"
                />
              </div>
            </div>
          )}

          <div className="newsletter-inner">
            <div className="newsletter-heading">
              <div className="newsletter-heading-desktop">
                {!isSideBar && (
                  <div className="newsletter-icon">
                    <img
                      src="/img/icon-send.svg"
                      alt=""
                      /* decorative */
                      data-testid="icon-newsletter"
                    />
                  </div>
                )}
              </div>
              <div className="newsletter-heading-mobile">
                {isSideBar && (
                  <div className="newsletter-icon">
                    <img
                      src="/img/icon-send.svg"
                      alt=""
                      /* decorative */
                      data-testid="icon-newsletter"
                    />
                  </div>
                )}
              </div>
              <div className="newsletter-heading-text">
                <h2 className="hdg-4">News you can use in your inbox</h2>
              </div>
            </div>
            {isSideBar && (
              <p data-testid="paragraph">
                When it comes to staying informed in Minnesota, our newsletters
                overdeliver. Sign-up now for headlines, breaking news, hometown
                stories, weather and much more. Delivered weekday mornings.
              </p>
            )}

            <form onSubmit={handleSubmit} className="newsletter-form">
              <div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  maxLength={254}
                  className="form-control"
                  required
                  disabled={isSubmitting}
                />
              </div>
              {message && (
                <div className={`message ${hasError ? 'error' : 'success'}`}>
                  {message}
                </div>
              )}
              <button
                type="submit"
                className="btn btn-primary btn-full-width"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
            {isSideBar && (
              <div className="newsletter-link">
                <Link
                  className="link"
                  href="https://www.americanpublicmedia.org/terms"
                  data-testid="termslink"
                  target="_blank"
                >
                  Terms
                </Link>
              </div>
            )}
          </div>
        </div>
      </>
    )
  );
};

export default NewsLetter;
